import {FormattedMessage} from 'react-intl';

import purify from 'dompurify';
import cloneDeep from 'lodash/cloneDeep';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import omit from 'lodash/omit';
import set from 'lodash/set';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import moment from 'moment';
import {v4 as uuid} from 'uuid';

import commonMessages from '@components/Common/CommonMessages/commonMessages';
import {defaultResponseMessages} from '@components/Common/ComposeMessageDrawer/statusConfirmationMessages';
import LocalizedModal from '@components/Common/LocalizedModal/LocalizedModal';
import {DEFAULT_PARENT_ID} from '@components/Organizations/constants';
import {SOCIAL_INTEGRATION_TYPE} from '@containers/Organizations/OrganizationProfilePage/tabs/OrganizationClientConfiguration/ClientConfigurationIntegrations/constants';
import {DEFAULT_WAIT_TIME} from '@containers/Organizations/OrganizationProfilePage/tabs/OrganizationClientConfiguration/ClientConfigurationMassNotification/NotificationOptions/DeliveryMethodRanking/deliveryRankingDefaults';

import {countRecipients} from '@hooks/services/communicator/useMessageTemplate';

import {
  ACCESS_CODE,
  ADDITIONAL_RECIPIENTS,
  ATTACHMENT_IDS,
  BULLETIN_BOARD_MESSAGE,
  CONFERENCE_BRIDGE,
  CONFERENCE_BRIDGE_CHECKED,
  CONFIG,
  DELIVERY_PRIORITY_RULE,
  DELIVERY_RULES,
  DESKTOP_ALERT_PROPERTIES,
  DRAWER_CONTEXT,
  EMAIL_MESSAGE_FIELDS,
  EMAIL_SURVEY_FIELDS,
  FORMATS,
  FORMATTED_REQUIRED_RECIPIENTS,
  getResponseTemplateFieldName,
  INTEGRATION_MESSAGES,
  INTERNAL_CONFERENCE_BRIDGE_CHECKED,
  INTERNAL_CONFERENCE_BRIDGE_FIELD,
  INTERNAL_CONFERENCE_BRIDGE_NUMBER,
  LANGUAGE,
  MANAGED_RECIPIENTS,
  MAX_CHAR_COUNT,
  MESSAGE_ACTIONS,
  MESSAGE_LANGUAGE,
  MESSAGE_MODALITIES,
  MESSAGE_MODALITY_LABELS,
  MESSAGE_TEMPLATE_FIELDS,
  MESSAGE_TEMPLATE_ID,
  MESSAGE_TYPE_FIELD,
  MESSAGE_TYPE_VALUES,
  MODALITY_TO_LABELS_MAP,
  OPTIONAL_RECIPIENTS,
  ORGANIZATION_ID,
  PERSON_GROUP_IDS,
  PERSON_IDS,
  PHONE_NUMBER,
  POST_INTEGRATIONS,
  POST_IS_COMBINED,
  POST_MESSAGE_CHECKBOX,
  PRIMARY_CONTACT_INFO_ONLY,
  QUOTA_CONFIGURATION,
  QUOTA_CONFIGURATION_FIELD,
  QUOTA_REQUIRED_RESPONSES_FIELD,
  RECIPIENT_COUNT,
  RECIPIENT_TYPE_ID_KEYS,
  RECIPIENTS,
  REQUIRE_STATUS_CONFIRMATION,
  REQUIRE_STATUS_CONFIRMATION_CHECKED,
  REQUIRED_RECIPIENTS,
  RESPONSE_OPTION_UNIQUE_IDS,
  RESPONSE_OPTIONS,
  SAVED_SEARCH_IDS,
  SCHEDULE,
  SENDER_RECIPIENT_OPTIONS,
  SITE_IDS,
  SITES,
  SLACK_INTEGRATION_CONFIG_ID,
  STANDARD_VARIABLES,
  SURVEY,
  TEAMS_INTEGRATION_CONFIG_ID,
  TEMPLATE_RECIPIENTS,
  TEMPORARY_ADDITIONAL_RECIPIENTS,
  TEMPORARY_MANAGED_RECIPIENTS,
  TIER_FIELD,
  TIERS,
  TYPE,
} from '@constants/communicator';
import {FEATURES} from '@constants/features';
import {SCHEDULE_DTO_ENUM} from '@constants/reports';

import localization from '@services/localization';

import {
  deliveryPriorityRuleToRankingItems,
  getDeliveryMethodRankingIdsFromLabels,
  mergeIdsIntoRanking,
  rankingItemsToDeliveryPriorityRule,
} from '@utils/deliveryMethodUtils/deliveryMethodUtils';
import {formatDurationFromHours} from '@utils/duration';
import {featureToggle} from '@utils/featureToggle/featureToggle';

import {COMMUNICATION_KEYS, FREQUENCY_TYPES, SCHEDULED_MESSAGE_TYPES} from '../../../constants/communications';
import {getAbbreviatedSortDirection} from '../admins/utils';
import messages from './messages';
import {convertPillsToVariablePlaceholdersInFormFields, extractStandardVariables} from './variables-converter.utils';

export const Z_INDEX_DRAWER = 1005;
export const Z_INDEX_CONFIRM_MODAL = Z_INDEX_DRAWER + 1;

/**
 * Normalizes person object by mapping primary contact fields to standard field names
 * @param {object} person - The person object with primary contact fields
 * @returns {object} The person object with normalized field names (firstName, lastName, email)
 */
export function normalizePrimaryContactFields(person) {
  return {
    ...person,
    firstName: person.primaryFirstName,
    lastName: person.primaryLastName,
    email: person.primaryEmail,
  };
}

/**
 * Extracts modality labels from formats object and maps them to the correct field names
 * @param {object} formats - The formats object containing modality configurations
 * @returns {object} An object with label field names as keys and their arrays of labels as values
 */
export function extractModalityLabelsFromFormats(formats) {
  const modalityLabelsData = {};

  if (!isEmpty(formats)) {
    Object.entries(MODALITY_TO_LABELS_MAP).forEach(([modality, labelField]) => {
      const modalityFormat = formats[modality];
      modalityLabelsData[labelField] = modalityFormat?.labels ? modalityFormat.labels : [];
    });
  }
  return modalityLabelsData;
}

/**
 * Creates an object with all modality label fields initialized to empty arrays
 * @returns {object} An object with dynamic label field names as keys and empty arrays as values
 */
export function getEmptyModalityLabels() {
  const emptyLabels = {};
  Object.values(MESSAGE_MODALITY_LABELS).forEach((labelField) => {
    emptyLabels[labelField] = [];
  });
  return emptyLabels;
}

export function getInternalConferenceBridgeFormFields(internalConferenceBridge) {
  const phoneNumber = internalConferenceBridge?.phoneNumbers?.[0];
  return {[INTERNAL_CONFERENCE_BRIDGE_CHECKED]: !!phoneNumber, [INTERNAL_CONFERENCE_BRIDGE_NUMBER]: phoneNumber};
}

export function transformComposeMessageTemplateToMessageTemplateDrawer(template) {
  if (!template) return null;
  const {
    messageModalities,
    managedRecipients,
    formats = {},
    survey,
    statusConfirmationOptions,
    conferenceBridge,
    [INTERNAL_CONFERENCE_BRIDGE_CHECKED]: internalConferenceBridgeChecked,
    [INTERNAL_CONFERENCE_BRIDGE_NUMBER]: internalConferenceBridgeNumber,
    subject,
    name,
    description,
    id,
    disabled,
    organizationId,
    primaryContactInfoOnly = false,
    autoTranslationDisabled = false,
    postMessageCheckbox,
    integrations,
    isPostCombined,
    bulletinBoardMessage,
    slackIntegrationConfigId,
    teamsIntegrationConfigId,
    integrationMessages,
    deliveryRanking,
    deliveryWaitTime,
    [MESSAGE_TYPE_FIELD]: messageType,
    [QUOTA_REQUIRED_RESPONSES_FIELD]: quotaRequiredResponses,
    [QUOTA_CONFIGURATION]: quotaConfiguration,
    [TIERS]: tiers,
  } = template;
  const modalityLabelsData = {};
  Object.values(MESSAGE_MODALITY_LABELS).forEach((labelField) => {
    modalityLabelsData[labelField] = template[labelField] || [];
  });

  const emailFormat = formats.emailFormat || {};
  const pushFormat = formats.pushFormat || {};
  const smsFormat = formats.smsFormat || {};
  const ttsFormat = formats.ttsFormat || {};
  const desktopAlertFormat = formats.desktopAlertFormat || {};

  const messageBody =
    emailFormat.messageBody ||
    pushFormat.messageBody ||
    smsFormat.messageBody ||
    ttsFormat.messageBody ||
    desktopAlertFormat?.messageBody ||
    template?.messageBody ||
    '';

  let channelIds = {};
  if (integrations?.length > 0) {
    channelIds = Object.fromEntries(
      integrations?.map((integrationKey) => [integrationKey, template[integrationKey] || []]),
    );
  }

  const internalConferenceBridge =
    internalConferenceBridgeChecked && internalConferenceBridgeNumber
      ? {phoneNumbers: [internalConferenceBridgeNumber]}
      : undefined;

  const messageActions = statusConfirmationOptions?.[MESSAGE_ACTIONS];
  const formattedStatusConfirmationOptions = statusConfirmationOptions
    ? omit(statusConfirmationOptions, [MESSAGE_ACTIONS])
    : statusConfirmationOptions;

  return {
    messageTemplate: id,
    organizationId,
    managedRecipients: managedRecipients,
    templateName: name ?? template.templateName ?? '',
    description: description ?? '',
    subject: subject ?? emailFormat.subject ?? pushFormat.subject ?? '',
    messageBody,
    messageModalities: messageModalities || [],
    disabled: disabled ?? false,
    messageConfig: {
      formats: {
        combined: formats.combined ?? false,
        primaryContactInfoOnly: formats.primaryContactInfoOnly ?? false,
        emailFormat: {
          ...emailFormat,
          subject: subject ?? emailFormat.subject ?? '',
        },
        pushFormat: {
          ...pushFormat,
          subject: subject ?? pushFormat.subject ?? '',
        },
        smsFormat,
        ttsFormat,
        desktopAlertFormat: {
          ...desktopAlertFormat,
          subject: subject ?? desktopAlertFormat.subject ?? '',
        },
      },
      survey: survey ?? {},
      statusConfirmationOptions: formattedStatusConfirmationOptions,
      conferenceBridge,
      [INTERNAL_CONFERENCE_BRIDGE_FIELD]: internalConferenceBridge,
      ...(messageActions?.length > 0 && {[MESSAGE_ACTIONS]: messageActions}),
    },
    formats: {
      combined: formats.combined ?? false,
      primaryContactInfoOnly: formats.primaryContactInfoOnly ?? false,
      emailFormat: {
        ...emailFormat,
        subject: subject ?? emailFormat.subject ?? '',
      },
      pushFormat: {
        ...pushFormat,
        subject: subject ?? pushFormat.subject ?? '',
      },
      smsFormat,
      ttsFormat,
      desktopAlertFormat: {
        ...desktopAlertFormat,
        subject: subject ?? desktopAlertFormat.subject ?? '',
      },
    },
    survey: survey ?? {},
    statusConfirmationOptions: formattedStatusConfirmationOptions,
    conferenceBridge,
    ...getInternalConferenceBridgeFormFields(internalConferenceBridge),
    ...modalityLabelsData,
    primaryContactInfoOnly,
    autoTranslationDisabled,
    // Posting options fields
    postMessageCheckbox,
    integrations,
    isPostCombined,
    bulletinBoardMessage,
    slackIntegrationConfigId,
    teamsIntegrationConfigId,
    integrationMessages,
    ...channelIds,
    deliveryRanking,
    deliveryWaitTime,
    [MESSAGE_TYPE_FIELD]: messageType ?? MESSAGE_TYPE_VALUES.MESSAGE,
    ...(messageType === MESSAGE_TYPE_VALUES.QUOTA && {
      [QUOTA_REQUIRED_RESPONSES_FIELD]: quotaRequiredResponses,
      [QUOTA_CONFIGURATION]: quotaConfiguration,
      ...(tiers && {[TIERS]: tiers}),
    }),
  };
}

export function transformNewMessageTemplateToComposeMessageTemplate(input) {
  if (!input) return null;

  const template = input.template ?? input;
  const {
    id,
    messageConfig = {},
    formats: topLevelFormats = {},
    messageModalities = [],
    subject: topLevelSubject,
    messageBody: topLevelMessageBody,
    autoTranslationDisabled = false,
    postMessageCheckbox,
    integrations,
    isPostCombined,
    bulletinBoardMessage,
    slackIntegrationConfigId,
    teamsIntegrationConfigId,
    integrationMessages,
    deliveryRanking,
    deliveryWaitTime,
  } = template;

  const formats = messageConfig.formats || topLevelFormats || {};

  const modalityLabelsData = extractModalityLabelsFromFormats(formats);

  const {
    combined = false,
    emailFormat,
    pushFormat,
    smsFormat,
    ttsFormat,
    desktopAlertFormat,
    primaryContactInfoOnly = false,
  } = formats;

  // Calculate messageModalities
  const modalities =
    messageModalities.length > 0
      ? messageModalities
      : [
          emailFormat && 'emailFormat',
          pushFormat && 'pushFormat',
          smsFormat && 'smsFormat',
          ttsFormat && 'ttsFormat',
          desktopAlertFormat && 'desktopAlertFormat',
        ].filter(Boolean);

  const transformedFormats = {
    combined,
    primaryContactInfoOnly: primaryContactInfoOnly ?? false,
    ...(emailFormat
      ? {
          emailFormat: {
            customFromName: emailFormat.customFromName,
            subject: emailFormat.subject,
            messageBody: emailFormat.messageBody,
            // Preserve attachmentIds if present
            ...(emailFormat.attachmentIds ? {attachmentIds: emailFormat.attachmentIds} : {}),
          },
        }
      : {}),
    ...(pushFormat
      ? {
          pushFormat: {
            subject: pushFormat.subject,
            messageBody: pushFormat.messageBody,
          },
        }
      : {}),
    ...(smsFormat
      ? {
          smsFormat: {
            subject: smsFormat.subject,
            messageBody: smsFormat.messageBody,
          },
        }
      : {}),
    ...(ttsFormat
      ? {
          ttsFormat: {
            subject: ttsFormat.subject,
            messageBody: ttsFormat.messageBody,
          },
        }
      : {}),
    ...(desktopAlertFormat
      ? {
          desktopAlertFormat: {
            subject: desktopAlertFormat.subject || topLevelSubject,
            messageBody: desktopAlertFormat.messageBody,
            [DESKTOP_ALERT_PROPERTIES.SIZE]: desktopAlertFormat[DESKTOP_ALERT_PROPERTIES.SIZE],
            [DESKTOP_ALERT_PROPERTIES.COLOR]: desktopAlertFormat[DESKTOP_ALERT_PROPERTIES.COLOR],
            [DESKTOP_ALERT_PROPERTIES.ALERT_SOUND]: desktopAlertFormat[DESKTOP_ALERT_PROPERTIES.ALERT_SOUND],
          },
        }
      : {}),
  };

  const subject =
    topLevelSubject ||
    emailFormat?.subject ||
    pushFormat?.subject ||
    smsFormat?.subject ||
    ttsFormat?.subject ||
    desktopAlertFormat?.subject ||
    '';

  const messageBody =
    topLevelMessageBody ||
    emailFormat?.messageBody ||
    pushFormat?.messageBody ||
    smsFormat?.messageBody ||
    ttsFormat?.messageBody ||
    desktopAlertFormat?.messageBody ||
    '';

  // Status confirmation
  const statusOptions = messageConfig.statusConfirmationOptions;
  const statusConfirmationOptionsChecked = !!statusOptions;
  const responseOptions = statusOptions?.responseOptions || [];

  const okResponse = responseOptions.find((opt) => opt.number === 1)?.text;
  const needHelpResponse = responseOptions.find((opt) => opt.number === 2)?.text;

  // Quota type message
  const quotaRule = messageConfig?.[DELIVERY_RULES]?.quotaRule;
  const quotaQuestion = messageConfig?.[SURVEY]?.[QUOTA_CONFIGURATION_FIELD.QUESTIONS]?.[0];
  const quotaResponseOptions = quotaQuestion?.[QUOTA_CONFIGURATION_FIELD.RESPONSE_OPTIONS] || [];
  const quotaPositiveResponse = quotaResponseOptions.find(
    (option) => option?.[QUOTA_CONFIGURATION_FIELD.NUMBER] === 1,
  )?.[QUOTA_CONFIGURATION_FIELD.TEXT];
  const quotaNegativeResponse = quotaResponseOptions.find(
    (option) => option?.[QUOTA_CONFIGURATION_FIELD.NUMBER] === 2,
  )?.[QUOTA_CONFIGURATION_FIELD.TEXT];

  // Conference bridge
  const conferenceBridge = messageConfig.conferenceBridge;
  const internalConferenceBridgeFields = getInternalConferenceBridgeFormFields(
    messageConfig[INTERNAL_CONFERENCE_BRIDGE_FIELD],
  );
  const conferenceBridgeChecked =
    !!conferenceBridge || internalConferenceBridgeFields[INTERNAL_CONFERENCE_BRIDGE_CHECKED];

  let channelIds = {};
  if (integrations?.length > 0) {
    channelIds = Object.fromEntries(
      integrations?.map((integrationKey) => [integrationKey, template[integrationKey] || []]),
    );
  }

  const templateData = {
    messageTemplate: id,
    messageModalities: modalities,
    formats: transformedFormats,
    subject,
    statusConfirmationOptionsChecked,
    messageBody,
    ...(statusConfirmationOptionsChecked && {
      statusConfirmationOptions: statusOptions,
      okResponse,
      needHelpResponse,
    }),
    conferenceBridgeChecked,
    ...(conferenceBridge && {
      conferenceBridge,
    }),
    ...internalConferenceBridgeFields,
    survey: messageConfig.survey ?? {},
    ...(quotaRule && {
      [MESSAGE_TYPE_FIELD]: MESSAGE_TYPE_VALUES.QUOTA,
      [QUOTA_REQUIRED_RESPONSES_FIELD]: quotaRule.maxDesiredResponses,
      [QUOTA_CONFIGURATION]: {
        [QUOTA_CONFIGURATION_FIELD.QUESTION]: quotaQuestion?.[QUOTA_CONFIGURATION_FIELD.TEXT],
        [QUOTA_CONFIGURATION_FIELD.POSITIVE_RESPONSE]: quotaPositiveResponse,
        [QUOTA_CONFIGURATION_FIELD.NEGATIVE_RESPONSE]: quotaNegativeResponse,
      },
      [TIERS]: messageConfig?.[DELIVERY_RULES]?.quotaRule?.tiers,
    }),
    ...modalityLabelsData,
    primaryContactInfoOnly,
    autoTranslationDisabled,
    // Posting options fields
    postMessageCheckbox,
    integrations,
    isPostCombined,
    bulletinBoardMessage,
    slackIntegrationConfigId,
    teamsIntegrationConfigId,
    integrationMessages,
    ...channelIds,
    deliveryRanking,
    deliveryWaitTime,
  };
  return templateData;
}

export const parseMessageTemplates = (messageTemplates) => {
  if (!isEmpty(messageTemplates)) {
    const parsed = messageTemplates.map((template) => {
      try {
        // Template content entered via template-ms uses single quotes for each key/value pair (for ease of input) -
        // we need to replace those single quotes with double quotes for proper JSON format.
        // We do not want to replace embedded single or double quotes in the content, after replacing all single quotes with double quotes -
        // - we will put the embedded single quote, \\', back
        // - we will put the embedded double quote, \\\\', back
        const updatedTemplateContent = template.content
          .replace(/'/g, '"')
          .replace(/([^\\])\\"/g, "$1'")
          .replace(/\\\\"/g, '\\"');
        const parsedContent = JSON.parse(updatedTemplateContent);
        template = {
          ...template,
          content: parsedContent,
        };

        return template;
      } catch {
        return null;
      }
    });

    return parsed.filter((x) => x);
  }
};

function getMessageTemplateId(formFields = {}, scheduledMessage = {}) {
  return formFields?.[MESSAGE_TEMPLATE_ID] ?? scheduledMessage?.[MESSAGE_TEMPLATE_ID];
}

export function formatMessageForSetScheduleApi(formFields) {
  const schedule = cloneDeep(formFields[SCHEDULE]);
  schedule[SCHEDULE_DTO_ENUM.runAtTime] = moment.utc(schedule[SCHEDULE_DTO_ENUM.runAtTime]).format('HH:mm');

  const convertedFormFields = convertPillsToVariablePlaceholdersInFormFields(formFields);
  const standardVariables = extractStandardVariablesFromFormFields(convertedFormFields);
  const config = {
    ...formatMessageForApi({...formFields, attachmentIds: formFields.attachmentIds}),
    [ORGANIZATION_ID]: formFields[ORGANIZATION_ID],
  };
  const isQuotaMessage = Boolean(config?.[DELIVERY_RULES]?.quotaRule);
  const messageTemplateId = getMessageTemplateId(formFields);

  return {
    [CONFIG]: config,
    [SCHEDULE]: schedule,
    [LANGUAGE]: formFields.messageLanguage,
    [TYPE]: SCHEDULED_MESSAGE_TYPES.RECURRING,
    [RECIPIENT_COUNT]: formFields[RECIPIENT_COUNT],
    ...(messageTemplateId && {[MESSAGE_TEMPLATE_ID]: messageTemplateId}),
    ...(!isQuotaMessage && {[MANAGED_RECIPIENTS]: formFields[MANAGED_RECIPIENTS]}),
    [STANDARD_VARIABLES]: standardVariables,
  };
}

export function formatMessageForSendLaterApi(formFields) {
  const startDateString = formFields.schedule.startDate.toISOString();
  const startOfDay = moment.utc(startDateString).startOf('day').toISOString();
  const endOfDay = moment.utc(startDateString).endOf('day').toISOString();
  const selectedTime = moment.utc(startDateString).format('HH:mm:ss');

  const convertedFormFields = convertPillsToVariablePlaceholdersInFormFields(formFields);
  const standardVariables = extractStandardVariablesFromFormFields(convertedFormFields);
  const config = {
    ...formatMessageForApi({...formFields, attachmentIds: formFields.attachmentIds}),
    [ORGANIZATION_ID]: formFields[ORGANIZATION_ID],
  };
  const isQuotaMessage = Boolean(config?.[DELIVERY_RULES]?.quotaRule);
  const messageTemplateId = getMessageTemplateId(formFields);

  return {
    [CONFIG]: config,
    [SCHEDULE]: {
      [SCHEDULE_DTO_ENUM.frequencyType]: FREQUENCY_TYPES.DAILY,
      [SCHEDULE_DTO_ENUM.repeatDailyInterval]: 1,
      [SCHEDULE_DTO_ENUM.startDate]: startOfDay,
      [SCHEDULE_DTO_ENUM.endDate]: endOfDay,
      [SCHEDULE_DTO_ENUM.runAtTime]: selectedTime,
    },
    [LANGUAGE]: formFields.messageLanguage,
    [TYPE]: SCHEDULED_MESSAGE_TYPES.SCHEDULED,
    [RECIPIENT_COUNT]: formFields[RECIPIENT_COUNT],
    ...(messageTemplateId && {[MESSAGE_TEMPLATE_ID]: messageTemplateId}),
    ...(!isQuotaMessage && {[MANAGED_RECIPIENTS]: formFields[MANAGED_RECIPIENTS]}),
    [STANDARD_VARIABLES]: standardVariables,
  };
}

export function formatMessageForApiWithEnhancedRecipients(formFields) {
  const managedRecipients = formFields[MANAGED_RECIPIENTS];

  const messageTemplateId = getMessageTemplateId(formFields);

  if (managedRecipients) {
    managedRecipients[PERSON_IDS] = managedRecipients[PERSON_IDS].map((recipient) => recipient.id || recipient);
    managedRecipients[PERSON_GROUP_IDS] = managedRecipients[PERSON_GROUP_IDS].map(
      (recipient) => recipient.id || recipient,
    );
    managedRecipients[SAVED_SEARCH_IDS] = managedRecipients[SAVED_SEARCH_IDS].map(
      (recipient) => recipient.id || recipient,
    );
  }

  const convertedFormFields = convertPillsToVariablePlaceholdersInFormFields(formFields);
  const standardVariables = extractStandardVariablesFromFormFields(convertedFormFields);
  const messageConfig = formatMessageForApi(formFields);
  const isQuotaMessage = Boolean(messageConfig?.[DELIVERY_RULES]?.quotaRule);

  return {
    ...(managedRecipients && !isQuotaMessage && {[MANAGED_RECIPIENTS]: managedRecipients}),
    messageLanguage: formFields.messageLanguage,
    messageConfig,
    ...(messageTemplateId && {[MESSAGE_TEMPLATE_ID]: messageTemplateId}),
    organizationId: formFields.organizationId,
    messageId: uuid(),
    messageType: 'MANUAL',
    standardVariables,
  };
}

/**
 * Send-time fallback: computes a delivery ranking when the user never explicitly set one.
 * Builds a ranking from the org's default config, filtered to the form's selected modalities.
 *
 * Unlike the UI-side getFilteredModalityRanking (which always has labels from component state),
 * this must handle the case where label form fields are empty — it reverse-engineers labels
 * from the org default ranking IDs (e.g. "emailFormat-PERSONAL" → "PERSONAL").
 *
 * @param {object} formFields - The form fields at send time
 * @returns {object|null} A ranking object like {rank1: [...], rank2: [...]}, or null if insufficient data
 */
function computeDefaultRankingFromFormFields(formFields) {
  const selectedModalities = formFields[EMAIL_MESSAGE_FIELDS.messageModalities];
  const defaultRanking = formFields[EMAIL_MESSAGE_FIELDS.defaultDeliveryRanking];

  if (!selectedModalities?.length || !defaultRanking) {
    return null;
  }

  // Build a labels map: {emailFormat: ["personal", "work"], smsFormat: ["mobile"]}
  const filteredLabels = {};
  selectedModalities.forEach((modality) => {
    const labelField = MODALITY_TO_LABELS_MAP[modality];
    const labels = labelField ? formFields[labelField] || [] : [];

    if (labels.length > 0) {
      filteredLabels[modality] = labels;
    } else {
      // Labels not set in form (user never touched modalities) — extract all labels
      // for this modality from the org's default ranking IDs
      // e.g. "emailFormat-PERSONAL" → "PERSONAL"
      const allDefaultIds = Object.values(defaultRanking).flat();
      const modalityIds = allDefaultIds.filter((id) => id === modality || id.startsWith(`${modality}-`));
      const extractedLabels = modalityIds
        .map((id) => (id === modality ? null : id.slice(modality.length + 1)))
        .filter(Boolean);
      filteredLabels[modality] = extractedLabels;
    }
  });

  // Convert labels to delivery method IDs and merge into org default ranking positions
  const deliveryMethodIds = getDeliveryMethodRankingIdsFromLabels(filteredLabels);
  if (deliveryMethodIds.length === 0) {
    return null;
  }

  return mergeIdsIntoRanking(deliveryMethodIds, defaultRanking);
}

function normalizeManagedRecipientsForQuota(managedRecipients) {
  const formattedRecipients = formatRecipientsForApi(managedRecipients) || {};

  return {
    [PERSON_IDS]: formattedRecipients[PERSON_IDS] || [],
    [PERSON_GROUP_IDS]: formattedRecipients[PERSON_GROUP_IDS] || [],
    [SAVED_SEARCH_IDS]: formattedRecipients[SAVED_SEARCH_IDS] || [],
    [SITES]: formattedRecipients[SITES] || [],
  };
}

function getTierRecipientsForApi(tier) {
  const added = normalizeManagedRecipientsForQuota(tier?.[TIER_FIELD.MANAGED_RECIPIENTS]);
  const locked = normalizeManagedRecipientsForQuota(tier?.[TIER_FIELD.LOCKED_RECIPIENTS]);

  return {
    [PERSON_IDS]: [...new Set([...locked[PERSON_IDS], ...added[PERSON_IDS]])],
    [PERSON_GROUP_IDS]: [...new Set([...locked[PERSON_GROUP_IDS], ...added[PERSON_GROUP_IDS]])],
    [SAVED_SEARCH_IDS]: [...new Set([...locked[SAVED_SEARCH_IDS], ...added[SAVED_SEARCH_IDS]])],
    [SITES]: uniqBy([...locked[SITES], ...added[SITES]], 'siteId'),
  };
}

function buildQuotaRule(formFields) {
  const maxDesiredResponses = formFields?.[QUOTA_REQUIRED_RESPONSES_FIELD];
  const quotaConfiguration = formFields?.[QUOTA_CONFIGURATION];
  const tiers = formFields?.[TIERS];

  if (isNil(maxDesiredResponses) || isEmpty(quotaConfiguration)) {
    return undefined;
  }

  // With HZMN_350 every tier holds its own recipients: empty tiers are dropped and the remaining ones renumbered.
  // Without it, quota recipients are edited as managedRecipients (like a regular message) and sent as a single tier.
  // Builds new objects on purpose - formFields come straight from the form store, and mutating them would leave
  // API-shaped recipients in the form if the send is cancelled (e.g. from the attachment warning).
  const formattedTiers =
    featureToggle(FEATURES.HZMN_350) && tiers?.length
      ? tiers
          .map(getTierRecipientsForApi)
          .filter(hasAnyManagedRecipients)
          .map((managedRecipients, index) => ({tierNumber: index + 1, managedRecipients}))
      : [{tierNumber: 1, managedRecipients: normalizeManagedRecipientsForQuota(formFields?.[MANAGED_RECIPIENTS])}];

  return {
    surveyQuestionNumber: 1,
    desiredResponseOptionNumber: 1,
    maxDesiredResponses,
    tiers: formattedTiers,
  };
}

function buildQuotaSurvey(formFields) {
  const quotaConfiguration = formFields?.[QUOTA_CONFIGURATION];

  if (isEmpty(quotaConfiguration)) {
    return undefined;
  }

  return {
    [QUOTA_CONFIGURATION_FIELD.QUESTIONS]: [
      {
        [QUOTA_CONFIGURATION_FIELD.NUMBER]: 1,
        [QUOTA_CONFIGURATION_FIELD.TEXT]: quotaConfiguration[QUOTA_CONFIGURATION_FIELD.QUESTION],
        [QUOTA_CONFIGURATION_FIELD.RESPONSE_OPTIONS]: [
          {
            [QUOTA_CONFIGURATION_FIELD.NUMBER]: 1,
            [QUOTA_CONFIGURATION_FIELD.TEXT]: quotaConfiguration[QUOTA_CONFIGURATION_FIELD.POSITIVE_RESPONSE],
          },
          {
            [QUOTA_CONFIGURATION_FIELD.NUMBER]: 2,
            [QUOTA_CONFIGURATION_FIELD.TEXT]: quotaConfiguration[QUOTA_CONFIGURATION_FIELD.NEGATIVE_RESPONSE],
          },
        ],
      },
    ],
  };
}

export function formatMessageForApi(formFields) {
  let convertedFields = cloneDeep(formFields);

  const isCombined = convertedFields[EMAIL_MESSAGE_FIELDS.formats][EMAIL_MESSAGE_FIELDS.combined];

  if (isCombined || !has(convertedFields, [EMAIL_MESSAGE_FIELDS.formats, EMAIL_MESSAGE_FIELDS.combined])) {
    convertedFields = addMessageBody(convertedFields);
  }

  const postFormats = buildPostFormats(convertedFields);

  const formattedData = omitUnnecessaryFields(
    addModalityLabels(addSurveyIndices(addConferenceBridge(addSubject(convertedFields)))),
  );

  // Add attachmentIds to emailFormat if present
  if (convertedFields?.attachmentIds && formattedData?.formats?.emailFormat) {
    formattedData.formats.emailFormat.attachmentIds = convertedFields.attachmentIds;
  }

  if (formattedData[REQUIRE_STATUS_CONFIRMATION]?.[RESPONSE_OPTIONS]?.length >= 1) {
    formattedData[REQUIRE_STATUS_CONFIRMATION].useDefaultResponseOptions = hasDefaultResponseOptions(
      formattedData[REQUIRE_STATUS_CONFIRMATION][RESPONSE_OPTIONS],
    );
  }

  if (formattedData[REQUIRE_STATUS_CONFIRMATION]?.[MESSAGE_ACTIONS]?.length > 0) {
    formattedData[MESSAGE_ACTIONS] = formattedData[REQUIRE_STATUS_CONFIRMATION][MESSAGE_ACTIONS];
  }
  delete formattedData[REQUIRE_STATUS_CONFIRMATION]?.[MESSAGE_ACTIONS];

  if (postFormats) {
    formattedData.postFormats = postFormats;
  }

  const internalConferenceBridgeNumber = formFields[INTERNAL_CONFERENCE_BRIDGE_NUMBER];
  if (internalConferenceBridgeNumber) {
    formattedData[INTERNAL_CONFERENCE_BRIDGE_FIELD] = {phoneNumbers: [internalConferenceBridgeNumber]};
  }

  const quotaRule = buildQuotaRule(formFields);
  if (quotaRule) {
    formattedData[DELIVERY_RULES] = {...formattedData[DELIVERY_RULES], quotaRule};
    formattedData[SURVEY] = buildQuotaSurvey(formFields);
  }

  const sendToPrimaryOnly = formattedData[EMAIL_MESSAGE_FIELDS.formats]?.[PRIMARY_CONTACT_INFO_ONLY];
  const statusConfirmationEnabled = !!formattedData[REQUIRE_STATUS_CONFIRMATION];

  if (statusConfirmationEnabled && formFields[EMAIL_MESSAGE_FIELDS.resendFrequencyDuration]) {
    formattedData.messageDuration = formatDurationFromHours(formFields[EMAIL_MESSAGE_FIELDS.resendFrequencyDuration]);
  }

  //If status confirmation is disabled or send to primary only is checked, we exclude deliveryRules from api request completely
  if (statusConfirmationEnabled && !sendToPrimaryOnly) {
    const rankingItems =
      formFields[EMAIL_MESSAGE_FIELDS.deliveryRanking] || computeDefaultRankingFromFormFields(formFields);
    if (rankingItems) {
      const waitTime = formFields[EMAIL_MESSAGE_FIELDS.deliveryWaitTime] ?? DEFAULT_WAIT_TIME;
      const deliveryPriorityRule = rankingItemsToDeliveryPriorityRule(rankingItems, waitTime);
      if (deliveryPriorityRule.priorities.length > 0) {
        formattedData[DELIVERY_RULES] = {[DELIVERY_PRIORITY_RULE]: deliveryPriorityRule};
      }
    }
  }

  return formattedData;
}

// Config Object used when building post formats for api, or converting from api payload to form object
export const POSTING_INTEGRATION_CONFIG_MAP = {
  [SOCIAL_INTEGRATION_TYPE.SLACK]: {
    formatKey: 'slackFormat',
    configIdKey: SLACK_INTEGRATION_CONFIG_ID,
  },
  [SOCIAL_INTEGRATION_TYPE.MS_TEAMS]: {
    formatKey: 'msTeamsFormat',
    configIdKey: TEAMS_INTEGRATION_CONFIG_ID,
  },
};

/**
 * Extracts posting options form fields from the config.postFormats structure for compose message form.
 * This reverses what buildPostFormats creates, converting API data back to form fields.
 * @param {object} postFormats - The postFormats object from config
 * @returns {object} Form fields for posting options
 */
export function extractPostingOptionsFromPostFormats(postFormats) {
  if (!postFormats) {
    return {
      [POST_MESSAGE_CHECKBOX]: false,
      [POST_INTEGRATIONS]: [],
      [POST_IS_COMBINED]: true,
      [INTEGRATION_MESSAGES]: {},
    };
  }

  const isCombined = postFormats.combined ?? true;
  const integrations = [];
  const integrationMessages = {};
  const channelIds = {};
  const configIds = {};
  let bulletinBoardMessage;

  Object.entries(POSTING_INTEGRATION_CONFIG_MAP).forEach(([integrationType, {formatKey, configIdKey}]) => {
    const format = postFormats[formatKey];
    if (format) {
      integrations.push(integrationType);
      channelIds[integrationType] = format.channelIds || [];
      configIds[configIdKey] = format.integrationConfigId;

      if (isCombined) {
        // In combined mode, all integrations share one message body via bulletinBoardMessage
        bulletinBoardMessage = bulletinBoardMessage || format.messageBody || '';
      } else {
        integrationMessages[integrationType] = format.messageBody || '';
      }
    }
  });

  return {
    [POST_MESSAGE_CHECKBOX]: integrations.length > 0,
    [POST_INTEGRATIONS]: integrations,
    [POST_IS_COMBINED]: isCombined,
    ...configIds,
    [INTEGRATION_MESSAGES]: integrationMessages,
    ...(isCombined && bulletinBoardMessage != null ? {[BULLETIN_BOARD_MESSAGE]: bulletinBoardMessage} : {}),
    ...channelIds,
  };
}

function buildPostFormats(formFields) {
  const postMessageCheckbox = formFields[POST_MESSAGE_CHECKBOX];
  const selectedIntegrations = formFields[POST_INTEGRATIONS] || [];

  if (!postMessageCheckbox || !selectedIntegrations.length) {
    return null;
  }

  const isCombined = formFields[POST_IS_COMBINED] ?? false;
  const channelIds = Object.fromEntries(
    selectedIntegrations.map((integrationKey) => [integrationKey, formFields[integrationKey] || []]),
  );

  const postFormats = {combined: isCombined};

  Object.entries(POSTING_INTEGRATION_CONFIG_MAP).forEach(([integrationType, {formatKey, configIdKey}]) => {
    if (selectedIntegrations.includes(integrationType)) {
      postFormats[formatKey] = {
        integrationConfigId: formFields[configIdKey],
        channelIds: channelIds[integrationType],
        messageBody: isCombined
          ? formFields[BULLETIN_BOARD_MESSAGE]
          : formFields[INTEGRATION_MESSAGES]?.[integrationType],
      };
    }
  });

  return postFormats;
}

function hasDefaultResponseOptions(responseOptions) {
  const defaultOkText = localization.formatMessage(defaultResponseMessages.okText);
  const defaultNeedHelpText = localization.formatMessage(defaultResponseMessages.needHelpText);
  const defaultAcknowledgeText = localization.formatMessage(defaultResponseMessages.acknowledgeText);
  if (responseOptions.length === 1) {
    return responseOptions[0]?.text === defaultAcknowledgeText;
  }
  return responseOptions[0]?.text === defaultOkText && responseOptions[1]?.text === defaultNeedHelpText;
}

export function formatMessageForUpdateApi(formFields) {
  const {
    templateName,
    [MANAGED_RECIPIENTS]: managedRecipients,
    [ADDITIONAL_RECIPIENTS]: additional,
    ...rest
  } = formFields;
  const isQuotaMessage =
    rest[MESSAGE_TYPE_FIELD] === MESSAGE_TYPE_VALUES.QUOTA ||
    (!isNil(rest[QUOTA_REQUIRED_RESPONSES_FIELD]) && !isEmpty(rest[QUOTA_CONFIGURATION]));

  const formattedData = formatMessageForApi({
    ...rest,
    ...(isQuotaMessage && managedRecipients ? {[MANAGED_RECIPIENTS]: managedRecipients} : {}),
  });

  const modalities = formFields[EMAIL_MESSAGE_FIELDS.messageModalities] || [];
  if (formFields.attachments && modalities.includes(MESSAGE_MODALITIES.EMAIL_FORMAT)) {
    formattedData.attachments = formFields.attachments;
  }

  return {templateName, messageConfig: formattedData};
}

function extractIds(recipients) {
  const result = {};
  if (!recipients) {
    return undefined;
  }

  Object.values(RECIPIENT_TYPE_ID_KEYS).forEach((key) => {
    if (Array.isArray(recipients[key]) && recipients[key].length > 0) {
      result[key] = recipients[key].map((recipient) => recipient.id || recipient);
    }
  });
  return Object.keys(result).length > 0 ? result : undefined;
}

function getSiteTypes(site) {
  if (isNil(site.includePOC) && isNil(site.includeOccupants)) {
    return site.siteTypes || [];
  }

  return [site.includePOC && 'POC', site.includeOccupants && 'OCCUPANT'].filter((siteType) => siteType);
}

//Formats managedRecipients from the scheduledMessage GET result to proper update schedule payload
function formatRecipientsForApi(managedRecipients) {
  if (!managedRecipients) {
    return managedRecipients;
  }
  const sitesData = managedRecipients[SITES] || managedRecipients[SITE_IDS];

  //Extract IDs from all recipient types for format --> {"recipientTypeIds": []}
  const result = extractIds(managedRecipients) || {};

  //Transform sites: map from siteIds key to {"sites": [{siteId : "", siteTypes: []}]}
  if (Array.isArray(sitesData) && sitesData.length > 0) {
    result[SITES] = sitesData.map((site) => ({
      siteId: site.siteId || site.id,
      siteTypes: getSiteTypes(site),
    }));
  }
  return result;
}

function extractIdsForTemplate(recipients) {
  const result = {};
  if (!recipients) {
    return undefined;
  }

  Object.values(RECIPIENT_TYPE_ID_KEYS).forEach((key) => {
    if (key !== SITE_IDS && Array.isArray(recipients[key]) && recipients[key].length > 0) {
      result[key] = recipients[key].map((recipient) => recipient.id || recipient);
    }
  });
  const sitesData = recipients.sites || recipients[SITE_IDS];
  if (Array.isArray(sitesData) && sitesData.length > 0) {
    result.sites = sitesData.map((site) => ({
      siteId: site.siteId || site.id,
      siteTypes: [...(site.includePOC ? ['POC'] : []), ...(site.includeOccupants ? ['OCCUPANT'] : [])],
    }));
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

function formatRecipients(formFields, context) {
  const recipientOptionType =
    formFields[MESSAGE_TEMPLATE_FIELDS.RECIPIENT_OPTION_SELECT] || SENDER_RECIPIENT_OPTIONS.CAN_ADD_RECIPIENTS;

  switch (context) {
    case DRAWER_CONTEXT.TEMPLATE:
      if (formFields[REQUIRED_RECIPIENTS] || formFields[OPTIONAL_RECIPIENTS]) {
        const canAddSpecificRecipientsSelected =
          recipientOptionType === SENDER_RECIPIENT_OPTIONS.CAN_ADD_SPECIFIC_RECIPIENTS;
        const senderRecipientOptions = {
          senderRecipientOptions: {
            type: recipientOptionType,
            [REQUIRED_RECIPIENTS]: extractIdsForTemplate(formFields[REQUIRED_RECIPIENTS]),

            // ignore optional recipients unless "can add specific recipients" is selected
            ...(canAddSpecificRecipientsSelected && {
              [OPTIONAL_RECIPIENTS]: extractIdsForTemplate(formFields[OPTIONAL_RECIPIENTS]),
            }),
          },
        };
        const hasRecipients = countRecipients(senderRecipientOptions);
        return hasRecipients ? senderRecipientOptions : undefined;
      }
      return {};
    case DRAWER_CONTEXT.COMPOSE_MESSAGE: {
      const result = extractIds(formFields[MANAGED_RECIPIENTS]);
      return result;
    }
    default:
      return {};
  }
}

export function formatMessageForApiWithDescription(formFields, customVariables) {
  const {templateName, description, ...rest} = formFields;
  const isQuotaTemplate = rest[MESSAGE_TYPE_FIELD] === MESSAGE_TYPE_VALUES.QUOTA;
  const quotaManagedRecipients = isQuotaTemplate ? extractIdsForTemplate(rest[REQUIRED_RECIPIENTS]) : undefined;
  const formattedRecipientOptions = isQuotaTemplate ? undefined : formatRecipients(rest, DRAWER_CONTEXT.TEMPLATE);
  const formFieldsForMessageConfig = quotaManagedRecipients
    ? {...rest, [MANAGED_RECIPIENTS]: quotaManagedRecipients}
    : rest;

  const convertedFormFields = convertPillsToVariablePlaceholdersInFormFields(rest);
  const standardVariables = extractStandardVariablesFromFormFields(convertedFormFields);
  const result = {
    templateName,
    description,
    customVariables,
    standardVariables,
    messageConfig: formatMessageForApi(formFieldsForMessageConfig),
    ...formattedRecipientOptions,
  };
  return result;
}

/**
 * Helper function to extract standard variables from all text fields in form data
 * @param {object} formFields - The form fields containing message content
 * @returns {object[]} Array of standard variable objects like [{type: "FIRST_NAME", placeholder: "{{FIRST_NAME}}"}]
 */
export function extractStandardVariablesFromFormFields(formFields) {
  if (!formFields) return [];

  const allVariables = new Map();

  // Check common text fields that may contain variables
  const textFields = [formFields[EMAIL_MESSAGE_FIELDS.subject], formFields[EMAIL_MESSAGE_FIELDS.messageBody]];

  // Check format-specific fields
  if (formFields[EMAIL_MESSAGE_FIELDS.formats]) {
    Object.values(formFields[EMAIL_MESSAGE_FIELDS.formats]).forEach((format) => {
      if (format?.subject) textFields.push(format.subject);
      if (format?.messageBody) textFields.push(format.messageBody);
    });
  }

  // Extract variables from all text fields
  textFields.forEach((text) => {
    if (text) {
      extractStandardVariables(text).forEach((variable) => {
        // Use type as key to avoid duplicates
        allVariables.set(variable.type, variable);
      });
    }
  });

  return Array.from(allVariables.values());
}

export function stripHtmlTags(htmlString) {
  return purify.sanitize(htmlString, {ALLOWED_TAGS: []});
}

/**
 * For use in COMBINED mode, Get the Max Character limit based on the selected modalities.
 *
 * The character limit in combined mode is the smallest limit of all the selected modalities.
 * For example, if SMS (limit: 277) and PUSH (limit: 3500) are selected,
 * the character limit will be the smallest of the two (which is the SMS limit of 277)
 */
export function getCombinedCharacterLimit(modalities = []) {
  return modalities.map((modality) => MAX_CHAR_COUNT[modality]).sort((a, b) => a - b)[0];
}

/**
 * For use in combined mode,
 * This takes the text entered into formFields.messageBody and duplicates it into each modality which is selected.
 * HTML tags are removed for all non-email modalities
 * @param formFields
 */
function addMessageBody(formFields) {
  const selectedModalities = formFields[EMAIL_MESSAGE_FIELDS.messageModalities] || [MESSAGE_MODALITIES.EMAIL_FORMAT];

  selectedModalities.forEach((selectedModality) => {
    let messageBody = formFields[EMAIL_MESSAGE_FIELDS.messageBody];

    set(formFields, [EMAIL_MESSAGE_FIELDS.formats, selectedModality, EMAIL_MESSAGE_FIELDS.messageBody], messageBody);
  });

  return formFields;
}

function omitUnnecessaryFields(formFields) {
  const survey = formFields[EMAIL_MESSAGE_FIELDS.survey];
  const conferenceBridge = formFields[CONFERENCE_BRIDGE];
  const isSurveyEmpty = isEmpty(survey) || isEmpty(survey[EMAIL_SURVEY_FIELDS.QUESTIONS]);
  const isConferenceBridgeEmpty = isEmpty(conferenceBridge) || isEmpty(conferenceBridge[PHONE_NUMBER]);

  if (formFields[EMAIL_MESSAGE_FIELDS.okResponse] && formFields[EMAIL_MESSAGE_FIELDS.needHelpResponse]) {
    formFields[REQUIRE_STATUS_CONFIRMATION][RESPONSE_OPTIONS][0].text = formFields[EMAIL_MESSAGE_FIELDS.okResponse];
    formFields[REQUIRE_STATUS_CONFIRMATION][RESPONSE_OPTIONS][1].text =
      formFields[EMAIL_MESSAGE_FIELDS.needHelpResponse];
  }

  const omittedFields = [
    ORGANIZATION_ID,
    MESSAGE_LANGUAGE,
    RECIPIENTS, // This field should be removed since it is not part of that payload and ignored by the service
    RECIPIENT_COUNT, // This field should be removed since it is not part of that payload and ignored by the service
    MANAGED_RECIPIENTS,
    TEMPLATE_RECIPIENTS,
    FORMATTED_REQUIRED_RECIPIENTS,
    TEMPORARY_MANAGED_RECIPIENTS,
    TEMPORARY_ADDITIONAL_RECIPIENTS,
    ATTACHMENT_IDS,
    EMAIL_MESSAGE_FIELDS.messageModalities,
    PHONE_NUMBER,
    EMAIL_MESSAGE_FIELDS.messageTemplate,
    EMAIL_MESSAGE_FIELDS.subject,
    EMAIL_MESSAGE_FIELDS.messageBody,
    EMAIL_MESSAGE_FIELDS.okResponse,
    EMAIL_MESSAGE_FIELDS.needHelpResponse,
    MESSAGE_TYPE_FIELD,
    QUOTA_REQUIRED_RESPONSES_FIELD,
    QUOTA_CONFIGURATION,
    ...Object.values(MESSAGE_MODALITY_LABELS),
    SCHEDULE, // This is part of the form because it needs to be validated. However, it should only show up in a separate "schedule" property.
    REQUIRE_STATUS_CONFIRMATION_CHECKED, //used only for local form state tracking
    CONFERENCE_BRIDGE_CHECKED, //used only for local form state tracking
    INTERNAL_CONFERENCE_BRIDGE_CHECKED, //used only for local form state tracking
    INTERNAL_CONFERENCE_BRIDGE_FIELD, // rebuilt from the form number in formatMessageForApi so a stale template copy is never sent
    INTERNAL_CONFERENCE_BRIDGE_NUMBER, // converted to internalConferenceBridge.phoneNumbers in formatMessageForApi
    ...(isSurveyEmpty ? [EMAIL_MESSAGE_FIELDS.survey] : []), // omit empty survey object to avoid backend 422s
    ...(isConferenceBridgeEmpty ? [CONFERENCE_BRIDGE] : []), // omit empty conference bridge object to avoid backend 422s
    // Posting options - represented in postFormats
    POST_MESSAGE_CHECKBOX,
    POST_INTEGRATIONS,
    POST_IS_COMBINED,
    BULLETIN_BOARD_MESSAGE,
    SLACK_INTEGRATION_CONFIG_ID,
    TEAMS_INTEGRATION_CONFIG_ID,
    INTEGRATION_MESSAGES,
    SOCIAL_INTEGRATION_TYPE.SLACK,
    SOCIAL_INTEGRATION_TYPE.MS_TEAMS,
    // DeliveryRanking and deliveryWaitTime are the form fields - converted to deliveryRules (api-expected field) in formatMessageForApi
    EMAIL_MESSAGE_FIELDS.deliveryRanking,
    EMAIL_MESSAGE_FIELDS.deliveryWaitTime,
    EMAIL_MESSAGE_FIELDS.defaultDeliveryRanking,
    EMAIL_MESSAGE_FIELDS.templateApplied,
    // Converted to the ISO 8601 messageDuration field in formatMessageForApi
    EMAIL_MESSAGE_FIELDS.resendFrequencyDuration,
    // UI-only response driven messaging template dropdown form fields (see useResponseTemplateMenu's field)
    ...RESPONSE_OPTION_UNIQUE_IDS.map(getResponseTemplateFieldName),
    TIERS,
  ];

  const payload = omit(formFields, omittedFields);

  const surveyHasEmptyResponse = payload[EMAIL_MESSAGE_FIELDS.survey]?.questions?.find(
    (item) => item?.responseOptions?.length === 0,
  );
  if (surveyHasEmptyResponse) {
    payload[EMAIL_MESSAGE_FIELDS.survey]?.questions.forEach((item) => {
      if (item?.responseOptions?.length === 0) {
        item.responseOptions = undefined;
      }
    });
  }

  return payload;
}

export function addSurveyIndices(formFields) {
  const questions = formFields[EMAIL_MESSAGE_FIELDS.survey]?.[EMAIL_SURVEY_FIELDS.QUESTIONS];
  questions?.forEach((question, index) => {
    const questionNumber = index + 1;
    question[EMAIL_SURVEY_FIELDS.NUMBER] = questionNumber;

    const responses = question[EMAIL_SURVEY_FIELDS.RESPONSE_OPTIONS];
    if (!isEmpty(responses)) {
      responses.forEach((response, index) => {
        const responseNumber = index + 1;
        response[EMAIL_SURVEY_FIELDS.NUMBER] = responseNumber;
      });
    }
  });

  return formFields;
}

function addConferenceBridge(formFields) {
  const selectedModalities = formFields[EMAIL_MESSAGE_FIELDS.messageModalities];
  const conferenceBridge = formFields[CONFERENCE_BRIDGE];

  selectedModalities?.forEach((selectedModality) => {
    let messageBody = formFields[EMAIL_MESSAGE_FIELDS.formats][selectedModality][EMAIL_MESSAGE_FIELDS.messageBody];

    const conferenceBridgeText = getConferenceBridgeText(selectedModality, conferenceBridge);

    if (isString(conferenceBridgeText)) {
      messageBody = `${messageBody}${conferenceBridgeText}`;
      set(formFields, [EMAIL_MESSAGE_FIELDS.formats, selectedModality, EMAIL_MESSAGE_FIELDS.messageBody], messageBody);
    }
  });

  return formFields;
}

/**
 * @param modality - the modality
 * @param conferenceBridge {{phoneNumber: string, accessCode?: string}} -
 */
export function getConferenceBridgeText(modality, conferenceBridge) {
  let phoneNumber = conferenceBridge?.[PHONE_NUMBER];
  let accessCode = conferenceBridge?.[ACCESS_CODE]; //This access code is optional, so we need to account for that when we build the text

  const isSMS = modality === MESSAGE_MODALITIES.SMS_FORMAT;
  const isTTS = modality === MESSAGE_MODALITIES.TTS_FORMAT;

  if (isString(modality) && isString(phoneNumber)) {
    if (isSMS) {
      let dialInText = ` Dial-in ${phoneNumber}`;
      if (isString(accessCode)) {
        dialInText += `,${accessCode}`;
      }

      return dialInText;
    } else {
      //all other modalities

      if (isTTS) {
        //We add a space between each number in the phone number and access code so that the Text-To-Speech system will pronounce them correctly.
        phoneNumber = phoneNumber.split('').join(' ');
        accessCode = accessCode?.split('').join(' ');
      }

      let dialInText = ` Use the following number to access the conference bridge: ${phoneNumber}`;
      if (isString(accessCode)) {
        dialInText += `, Access code: ${accessCode}`;
      }

      return dialInText;
    }
  }
}

/**
 * Returns the conference bridge selected on the message, the internal one only carries a phone number
 * @param conferenceBridge {{phoneNumber: string, accessCode?: string}}
 * @param internalConferenceBridgeChecked {boolean}
 * @param internalConferenceBridgeNumber {string}
 */
export function getActiveConferenceBridge(
  conferenceBridge,
  internalConferenceBridgeChecked,
  internalConferenceBridgeNumber,
) {
  return internalConferenceBridgeChecked ? {[PHONE_NUMBER]: internalConferenceBridgeNumber} : conferenceBridge;
}

/**
 * Takes the subject text formFields[EMAIL_MESSAGE_FIELDS.subject]
 * and duplicates it into emailFormat and pushFormat (if these formats are selected)
 * @param formFields
 */
function addSubject(formFields) {
  const emailFormat = formFields[EMAIL_MESSAGE_FIELDS.formats]?.[MESSAGE_MODALITIES.EMAIL_FORMAT];
  if (!isNil(emailFormat?.[EMAIL_MESSAGE_FIELDS.messageBody])) {
    //email modality is selected, add the subject
    emailFormat[EMAIL_MESSAGE_FIELDS.subject] = formFields[EMAIL_MESSAGE_FIELDS.subject];
  }

  const pushFormat = formFields[EMAIL_MESSAGE_FIELDS.formats]?.[MESSAGE_MODALITIES.PUSH_FORMAT];
  if (!isEmpty(pushFormat?.[EMAIL_MESSAGE_FIELDS.messageBody])) {
    //push modality is selected, add the subject
    pushFormat[EMAIL_MESSAGE_FIELDS.subject] = formFields[EMAIL_MESSAGE_FIELDS.subject];
  }

  const desktopAlertFormat = formFields[EMAIL_MESSAGE_FIELDS.formats]?.[MESSAGE_MODALITIES.DESKTOP_FORMAT];
  if (!isEmpty(desktopAlertFormat?.[EMAIL_MESSAGE_FIELDS.messageBody])) {
    desktopAlertFormat[EMAIL_MESSAGE_FIELDS.subject] = formFields[EMAIL_MESSAGE_FIELDS.subject];
  }
  return formFields;
}

function addModalityLabels(formFields) {
  const selectedModalities = formFields[EMAIL_MESSAGE_FIELDS.messageModalities];
  selectedModalities?.forEach((modality) => {
    const labelFieldName = MODALITY_TO_LABELS_MAP[modality];
    if (labelFieldName && formFields[labelFieldName]) {
      const labels = formFields[labelFieldName];
      if (formFields?.[EMAIL_MESSAGE_FIELDS.formats][modality]) {
        formFields[EMAIL_MESSAGE_FIELDS.formats][modality].labels = labels;
      }
    }
  });
  return formFields;
}

function getSortableField(field) {
  if (field.includes(COMMUNICATION_KEYS.messageSentAt)) {
    field = COMMUNICATION_KEYS.sentAt;
  }
  if (field.includes(COMMUNICATION_KEYS.responseOption)) {
    field = COMMUNICATION_KEYS.surveyResponse;
  }

  return field;
}

/**
 *
 * @param {string} field - The field that will be sorted
 * @param {string} order - The order: one of constants.ASCEND or constants.DESCEND
 * @return {string} the field that can be specified in the sort API (e.g. 'emailAddress,asc')
 */
export function getSortString(field, order) {
  return `${getSortableField(field)},${getAbbreviatedSortDirection(order)}`;
}

function setTimeToStartOfDay(momentUtcDatetimeString) {
  return moment.utc(momentUtcDatetimeString).utcOffset(0, true).utc().startOf('day').toISOString();
}

function setTimeToEndOfDay(momentUtcDatetimeString) {
  return moment.utc(momentUtcDatetimeString).utcOffset(0, true).utc().endOf('day').toISOString();
}

export function formatFilter({
  organizationId,
  dateRange,
  authorPersonIds,
  recipientPersonIds,
  messageTypes,
  type,
  messageContent,
  channelTypes,
  clientId,
  correlationId,
  messageType,
  recipientStatuses,
}) {
  const from = Array.isArray(dateRange) ? setTimeToStartOfDay(dateRange[0]) : undefined;
  const to = Array.isArray(dateRange) ? setTimeToEndOfDay(dateRange[1]) : undefined;

  return {
    organizationId,
    dateRange: {from, to},
    authorPersonIds,
    recipientPersonIds,
    communicationType: type,
    messageTypes,
    messageContent,
    channelTypes,
    clientId,
    correlationId,
    messageType,
    recipientStatuses,
  };
}

export function showConfirmDiscardMessage({onOk, onCancel}) {
  LocalizedModal.confirm({
    maskClosable: true,
    zIndex: Z_INDEX_CONFIRM_MODAL,
    title: <FormattedMessage {...messages.confirmDiscard} />,
    content: <FormattedMessage {...messages.confirmDiscardContent} />,
    okText: <FormattedMessage {...commonMessages.ok} />,
    cancelText: <FormattedMessage {...commonMessages.cancel} />,
    onOk: () => {
      onOk?.();
    },
    onCancel: () => {
      onCancel?.();
    },
  });
}

function combineMessage(formFields) {
  const selectedModalities = formFields[EMAIL_MESSAGE_FIELDS.messageModalities] || [MESSAGE_MODALITIES.EMAIL_FORMAT];
  let messageArray = [];

  selectedModalities.forEach((selectedModality) => {
    let messageBody = formFields[EMAIL_MESSAGE_FIELDS.formats][selectedModality][EMAIL_MESSAGE_FIELDS.messageBody];
    messageBody = stripHtmlTags(messageBody);

    messageArray = [...messageArray, messageBody];
  });

  return messageArray;
}

export function getLongestMessage(formFields) {
  const isSeparated = formFields[EMAIL_MESSAGE_FIELDS.formats][EMAIL_MESSAGE_FIELDS.combined] === false;

  if (isSeparated) {
    const messageArray = combineMessage(formFields);
    // return the longest message
    return messageArray.reduce((longest, current) => (longest.length > current.length ? longest : current), '');
  } else {
    return stripHtmlTags(formFields[EMAIL_MESSAGE_FIELDS.messageBody]);
  }
}

export async function validateDate(_rule, date, startDate) {
  const startDateIsToday = startDate?.date() === moment.utc().date();
  if (startDateIsToday && date.isBefore(moment.utc())) {
    throw new Error('The selected date is TODAY and at a time that is in the past');
  }
}

/**
 * Should remove the conference bridge text in the message body for scheduled messages because this text will be re-appended
 * with the new conference bridge phone number on save
 * @param scheduledMessage
 */
export function removeConferenceBridgeTextFromScheduledMessage(scheduledMessage) {
  const formats = scheduledMessage?.config?.formats;
  const conferenceBridge = scheduledMessage?.config?.[CONFERENCE_BRIDGE];

  if (!isEmpty(conferenceBridge)) {
    if (scheduledMessage.messageBody) {
      const allModalities = [
        MESSAGE_MODALITIES.EMAIL_FORMAT,
        MESSAGE_MODALITIES.PUSH_FORMAT,
        MESSAGE_MODALITIES.SMS_FORMAT,
        MESSAGE_MODALITIES.TTS_FORMAT,
        MESSAGE_MODALITIES.DESKTOP_FORMAT,
      ];

      let messageBodyUpdated = false;
      allModalities.forEach((modality) => {
        if (!messageBodyUpdated) {
          const conferenceBridgeText = getConferenceBridgeText(modality, conferenceBridge);
          const indexOfConferenceBridgeText = scheduledMessage.messageBody.indexOf(conferenceBridgeText);

          if (indexOfConferenceBridgeText > 0) {
            scheduledMessage.messageBody = scheduledMessage.messageBody.slice(0, indexOfConferenceBridgeText);
            messageBodyUpdated = true;
          }
        }
      });
    }

    if (formats) {
      const selectedModalities = Object.keys(formats).filter((modality) => {
        const arrayOfAllModalities = [
          MESSAGE_MODALITIES.EMAIL_FORMAT,
          MESSAGE_MODALITIES.PUSH_FORMAT,
          MESSAGE_MODALITIES.SMS_FORMAT,
          MESSAGE_MODALITIES.TTS_FORMAT,
          MESSAGE_MODALITIES.DESKTOP_FORMAT,
        ];
        return arrayOfAllModalities.includes(modality);
      });

      selectedModalities.forEach((selectedModality) => {
        const conferenceBridgeText = getConferenceBridgeText(selectedModality, conferenceBridge);

        if (formats[selectedModality]?.messageBody) {
          const indexOfConferenceBridgeText = formats[selectedModality].messageBody.indexOf(conferenceBridgeText);
          if (indexOfConferenceBridgeText > 0) {
            formats[selectedModality].messageBody = formats[selectedModality].messageBody.slice(
              0,
              indexOfConferenceBridgeText,
            );
          }
        }
      });
    }
  }
}

export const formatSitesFromApi = (managedRecipients) => {
  if (!managedRecipients?.sites) {
    return managedRecipients;
  }
  return {
    ...managedRecipients,
    [SITE_IDS]: managedRecipients.sites.map((site) => ({
      id: site.id || site.siteId,
      siteId: site.siteId,
      includePOC: site.siteTypes?.includes('POC') || site.includePOC || false,
      includeOccupants: site.siteTypes?.includes('OCCUPANT') || site.includeOccupants || false,
    })),
  };
};

export function formatScheduleForForm(scheduledMessage) {
  removeConferenceBridgeTextFromScheduledMessage(scheduledMessage);
  const schedule = scheduledMessage?.schedule;
  const modalityLabelsData = extractModalityLabelsFromFormats(scheduledMessage?.config?.formats);
  const quotaRule = scheduledMessage?.config?.[DELIVERY_RULES]?.quotaRule;
  const quotaQuestion = scheduledMessage?.config?.[SURVEY]?.[QUOTA_CONFIGURATION_FIELD.QUESTIONS]?.[0];
  const quotaResponseOptions = quotaQuestion?.[QUOTA_CONFIGURATION_FIELD.RESPONSE_OPTIONS] || [];
  const quotaPositiveResponse = quotaResponseOptions.find(
    (option) => option?.[QUOTA_CONFIGURATION_FIELD.NUMBER] === 1,
  )?.text;
  const quotaNegativeResponse = quotaResponseOptions.find(
    (option) => option?.[QUOTA_CONFIGURATION_FIELD.NUMBER] === 2,
  )?.text;
  const startDate = schedule?.[SCHEDULE_DTO_ENUM.startDate]
    ? moment.utc(schedule[SCHEDULE_DTO_ENUM.startDate])
    : undefined;
  const endDate = schedule?.[SCHEDULE_DTO_ENUM.endDate] ? moment.utc(schedule[SCHEDULE_DTO_ENUM.endDate]) : undefined;

  function mergeDateTime() {
    const runAtTime = schedule?.[SCHEDULE_DTO_ENUM.runAtTime];
    if (!startDate || !runAtTime) return undefined;

    const [hour, minute, second] = runAtTime.split(':').map(Number);
    return startDate.clone().set({hour, minute, second});
  }

  const modalities = [];
  if (scheduledMessage?.config?.formats) {
    const formats = scheduledMessage.config.formats;
    if (formats.emailFormat) modalities.push(MESSAGE_MODALITIES.EMAIL_FORMAT);
    if (formats.pushFormat) modalities.push(MESSAGE_MODALITIES.PUSH_FORMAT);
    if (formats.smsFormat) modalities.push(MESSAGE_MODALITIES.SMS_FORMAT);
    if (formats.ttsFormat) modalities.push(MESSAGE_MODALITIES.TTS_FORMAT);
    if (formats.desktopAlertFormat) modalities.push(MESSAGE_MODALITIES.DESKTOP_FORMAT);
  }
  const statusOptions = scheduledMessage?.config?.statusConfirmationOptions;
  const statusConfirmationOptionsChecked = !!statusOptions;
  const responseOptions = statusOptions?.responseOptions || [];

  const okResponse = responseOptions.find((opt) => opt.number === 1)?.text;
  const needHelpResponse = responseOptions.find((opt) => opt.number === 2)?.text;

  const conferenceBridge = scheduledMessage?.config?.conferenceBridge;
  const internalConferenceBridgeFields = getInternalConferenceBridgeFormFields(
    scheduledMessage?.config?.[INTERNAL_CONFERENCE_BRIDGE_FIELD],
  );
  const conferenceBridgeChecked =
    !!conferenceBridge || internalConferenceBridgeFields[INTERNAL_CONFERENCE_BRIDGE_CHECKED];

  const postingOptions = extractPostingOptionsFromPostFormats(scheduledMessage?.config?.postFormats);

  const deliveryPriorityRule = scheduledMessage?.config?.[DELIVERY_RULES]?.[DELIVERY_PRIORITY_RULE];
  const deliveryRankingData = deliveryPriorityRule ? deliveryPriorityRuleToRankingItems(deliveryPriorityRule) : {};

  const formattedTypeMap = {
    [SCHEDULED_MESSAGE_TYPES.RECURRING]: {
      [SCHEDULE]: {
        ...schedule,
        [SCHEDULE_DTO_ENUM.startDate]: startDate,
        [SCHEDULE_DTO_ENUM.endDate]: endDate,
        [SCHEDULE_DTO_ENUM.runAtTime]: schedule?.[SCHEDULE_DTO_ENUM.runAtTime]
          ? moment.utc(schedule[SCHEDULE_DTO_ENUM.runAtTime], 'HH:mm')
          : undefined,
      },
    },
    [SCHEDULED_MESSAGE_TYPES.SCHEDULED]: {
      [SCHEDULE]: {
        [SCHEDULE_DTO_ENUM.startDate]: mergeDateTime(),
      },
    },
  };

  // Transform managedRecipients.sites from API format to UI format for form
  const transformedScheduledMessage = {...scheduledMessage};
  //If a quota message without tiers, we load the recipients from the first tier to the form's managed recipients field
  if (
    !transformedScheduledMessage.managedRecipients &&
    quotaRule?.tiers?.[0]?.managedRecipients &&
    !featureToggle(FEATURES.HZMN_350)
  ) {
    transformedScheduledMessage.managedRecipients = quotaRule.tiers[0].managedRecipients;
  }
  //If tiers are enabled, extract the tiers from quote rule to the form's "tiers" field, if we haven't already
  if (!transformedScheduledMessage.tiers?.length && featureToggle(FEATURES.HZMN_350) && quotaRule?.tiers?.length) {
    transformedScheduledMessage.tiers = quotaRule.tiers;
    transformedScheduledMessage.tiers.forEach((tier) => {
      tier.managedRecipients = formatSitesFromApi(tier.managedRecipients);
      delete tier.managedRecipients.sites;
    });
    //Clear regular managed recipients since we are using the tier recipients instead
    delete transformedScheduledMessage.managedRecipients;
  }

  if (transformedScheduledMessage.managedRecipients?.sites) {
    transformedScheduledMessage.managedRecipients = formatSitesFromApi(transformedScheduledMessage.managedRecipients);
    delete transformedScheduledMessage.managedRecipients.sites;
  }

  return {
    ...transformedScheduledMessage,
    ...formattedTypeMap[scheduledMessage?.type],
    [EMAIL_MESSAGE_FIELDS.messageModalities]: modalities,
    ...modalityLabelsData,
    [REQUIRE_STATUS_CONFIRMATION_CHECKED]: statusConfirmationOptionsChecked,
    ...(statusConfirmationOptionsChecked && {
      [REQUIRE_STATUS_CONFIRMATION]: statusOptions,
      [EMAIL_MESSAGE_FIELDS.okResponse]: okResponse,
      [EMAIL_MESSAGE_FIELDS.needHelpResponse]: needHelpResponse,
    }),
    [CONFERENCE_BRIDGE_CHECKED]: conferenceBridgeChecked,
    ...(conferenceBridge && {
      [CONFERENCE_BRIDGE]: conferenceBridge,
    }),
    ...internalConferenceBridgeFields,
    ...(quotaRule && {
      [QUOTA_REQUIRED_RESPONSES_FIELD]: quotaRule.maxDesiredResponses,
      [QUOTA_CONFIGURATION]: {
        [QUOTA_CONFIGURATION_FIELD.QUESTION]: quotaQuestion?.[QUOTA_CONFIGURATION_FIELD.TEXT],
        [QUOTA_CONFIGURATION_FIELD.POSITIVE_RESPONSE]: quotaPositiveResponse,
        [QUOTA_CONFIGURATION_FIELD.NEGATIVE_RESPONSE]: quotaNegativeResponse,
      },
    }),
    ...postingOptions,
    deliveryRanking: deliveryRankingData.rankingItems,
    deliveryWaitTime: deliveryRankingData.waitTime,
  };
}

export function formatTemporaryRecipients(scheduledMessage) {
  const managedPersonIds = (scheduledMessage?.managedRecipients?.personIds || []).map((person) => person?.id || person);
  const managedGroupIds = (scheduledMessage?.managedRecipients?.personGroupIds || []).map(
    (group) => group?.id || group,
  );
  const managedSavedSearchIds = (scheduledMessage?.managedRecipients?.savedSearchIds || []).map(
    (savedSearch) => savedSearch?.id || savedSearch,
  );
  const shouldHydrateAdditionalRecipients =
    scheduledMessage?.senderRecipientOptions?.type === SENDER_RECIPIENT_OPTIONS.CAN_ADD_SPECIFIC_RECIPIENTS;

  return {
    temporaryAdditionalRecipients: shouldHydrateAdditionalRecipients
      ? [
          ...managedPersonIds.map((id) => `person:${id}`),
          ...managedGroupIds.map((id) => `group:${id}`),
          ...managedSavedSearchIds.map((id) => `savedSearch:${id}`),
        ]
      : [],

    temporaryManagedRecipients: [
      ...managedPersonIds.map((id) => `person:${id}`),
      ...managedGroupIds.map((id) => `group:${id}`),
      ...managedSavedSearchIds.map((id) => `savedSearch:${id}`),
    ],
    formattedRequiredRecipients: {
      personIds: managedPersonIds,
      personGroupIds: managedGroupIds,
      savedSearchIds: managedSavedSearchIds,
    },
  };
}

// service returns a 400 if startDate is in the past
// if the form isn't being updated (like updating message without schedule),
// need to set startDate to today's date
export function ensureValidSchedule(schedule) {
  if (!schedule?.startDate) return schedule;

  const startDate = moment.utc(schedule.startDate);
  const currentDatePlus5Minutes = moment.utc().add(5, 'minutes');

  if (startDate.isBefore(currentDatePlus5Minutes)) {
    return {
      ...schedule,
      startDate: currentDatePlus5Minutes.toISOString(),
    };
  }

  return schedule;
}

function normalizeManagedRecipientsForUpdate(managedRecipients = {}) {
  return {
    [PERSON_IDS]: managedRecipients?.[PERSON_IDS] || [],
    [PERSON_GROUP_IDS]: managedRecipients?.[PERSON_GROUP_IDS] || [],
    [SAVED_SEARCH_IDS]: managedRecipients?.[SAVED_SEARCH_IDS] || [],
    [SITES]: managedRecipients?.[SITES] || [],
  };
}

function hasAnyManagedRecipients(managedRecipients = {}) {
  return Object.values(managedRecipients).some((value) => Array.isArray(value) && value.length > 0);
}

function getFormFieldsWithScheduledQuotaRecipients(formFields, scheduledMessage) {
  //No need to apply quota tier[0] recipients to managed recipients if tier recipients are enabled
  if (featureToggle(FEATURES.HZMN_350)) {
    return formFields;
  }
  const scheduledQuotaManagedRecipients =
    scheduledMessage?.[CONFIG]?.[DELIVERY_RULES]?.quotaRule?.tiers?.[0]?.managedRecipients;

  if (!scheduledQuotaManagedRecipients) {
    return formFields;
  }

  const formManagedRecipients = normalizeManagedRecipientsForQuota(formFields?.[MANAGED_RECIPIENTS]);

  if (hasAnyManagedRecipients(formManagedRecipients)) {
    return formFields;
  }

  return {
    ...formFields,
    [MANAGED_RECIPIENTS]: scheduledQuotaManagedRecipients,
  };
}

// copied + modified from formatMessageForSetScheduleApi -- refactor later
export function formatForUpdateRecurringMessageApi({scheduledMessage, formFields, recipientCount}) {
  const formFieldsForConfig = getFormFieldsWithScheduledQuotaRecipients(formFields, scheduledMessage);
  const config = formFieldsForConfig[FORMATS]
    ? formatMessageForUpdateApi(formFieldsForConfig)?.messageConfig
    : scheduledMessage[CONFIG];
  const isQuotaMessage = Boolean(config?.[DELIVERY_RULES]?.quotaRule);
  const messageTemplateId = getMessageTemplateId(formFields, scheduledMessage);

  const validatedSchedule = ensureValidSchedule(scheduledMessage[SCHEDULE]);
  const scheduleObject = formFields[SCHEDULE] || validatedSchedule;

  const runAtTime = scheduleObject?.[SCHEDULE_DTO_ENUM.runAtTime];

  const formattedRunAtTime = moment.isMoment(scheduleObject?.[SCHEDULE_DTO_ENUM.runAtTime])
    ? runAtTime.format('HH:mm:ss')
    : runAtTime;

  const schedule = {
    ...scheduleObject,
    [SCHEDULE_DTO_ENUM.runAtTime]: formattedRunAtTime,
  };

  // When formFields doesn't have managedRecipients, this indicates a schedule-only edit (without going through compose message form)
  // In this case we need to specifically format the recipients payload to match update schedule API reqs
  const managedRecipients = formFields[MANAGED_RECIPIENTS]
    ? normalizeManagedRecipientsForUpdate(formFields[MANAGED_RECIPIENTS])
    : normalizeManagedRecipientsForUpdate(formatRecipientsForApi(scheduledMessage[MANAGED_RECIPIENTS]));
  const shouldIncludeManagedRecipients =
    hasAnyManagedRecipients(managedRecipients) || !scheduledMessage?.senderRecipientOptions;

  const convertedFormFields = convertPillsToVariablePlaceholdersInFormFields(formFields);
  const standardVariables = extractStandardVariablesFromFormFields(convertedFormFields);

  return {
    id: scheduledMessage.id,
    [CONFIG]: config,
    [SCHEDULE]: schedule,
    [LANGUAGE]: scheduledMessage[LANGUAGE],
    [TYPE]: SCHEDULED_MESSAGE_TYPES.RECURRING,
    [RECIPIENT_COUNT]: recipientCount || scheduledMessage[RECIPIENT_COUNT],
    ...(messageTemplateId && {[MESSAGE_TEMPLATE_ID]: messageTemplateId}),
    ...(shouldIncludeManagedRecipients && !isQuotaMessage && {[MANAGED_RECIPIENTS]: managedRecipients}),
    [STANDARD_VARIABLES]: standardVariables,
  };
}

// copied + modified from formatMessageForSendLaterApi -- refactor later
export function formatForUpdateSendLaterApi({scheduledMessage, formFields, recipientCount}) {
  const formFieldsForConfig = getFormFieldsWithScheduledQuotaRecipients(formFields, scheduledMessage);
  const config = formFieldsForConfig[FORMATS]
    ? formatMessageForUpdateApi(formFieldsForConfig)?.messageConfig
    : scheduledMessage[CONFIG];
  const isQuotaMessage = Boolean(config?.[DELIVERY_RULES]?.quotaRule);
  const messageTemplateId = getMessageTemplateId(formFields, scheduledMessage);

  const scheduleUpdatedInForm = !!formFields[SCHEDULE];

  let scheduleFromForm;
  if (scheduleUpdatedInForm) {
    const startDateString = formFields[SCHEDULE].startDate;
    const startOfDay = moment.utc(startDateString).startOf('day').toISOString();
    const endOfDay = moment.utc(startDateString).endOf('day').toISOString();

    const selectedTime = moment.isMoment(startDateString)
      ? moment.utc(startDateString).format('HH:mm:ss')
      : startDateString;

    scheduleFromForm = {
      [SCHEDULE_DTO_ENUM.frequencyType]: FREQUENCY_TYPES.DAILY,
      [SCHEDULE_DTO_ENUM.repeatDailyInterval]: 1,
      [SCHEDULE_DTO_ENUM.startDate]: startOfDay,
      [SCHEDULE_DTO_ENUM.endDate]: endOfDay,
      [SCHEDULE_DTO_ENUM.runAtTime]: selectedTime,
    };
  }

  const temporaryAdditionalRecipients = formFields.temporaryAdditionalRecipients;
  const temporaryManagedRecipients = formFields.temporaryManagedRecipients;

  // Helper: parse temporaryAdditionalRecipients or temporaryManagedRecipients strings into structured managedRecipients
  const parseTemporaryRecipients = (tempArray) => {
    const parsed = {
      [PERSON_IDS]: [],
      [PERSON_GROUP_IDS]: [],
      [SAVED_SEARCH_IDS]: [],
      [SITES]: [],
    };

    (tempArray || []).forEach((item) => {
      if (item.startsWith('person:')) {
        parsed[PERSON_IDS].push(item.replace('person:', ''));
      } else if (item.startsWith('group:')) {
        parsed[PERSON_GROUP_IDS].push(item.replace('group:', ''));
      } else if (item.startsWith('savedSearch:')) {
        parsed[SAVED_SEARCH_IDS].push(item.replace('savedSearch:', ''));
      } else if (item.startsWith('sites:')) {
        parsed[SAVED_SEARCH_IDS].push(item.replace('siteIds:', ''));
      }
    });

    return parsed;
  };

  let managedRecipients;

  if (temporaryAdditionalRecipients) {
    managedRecipients = parseTemporaryRecipients(temporaryAdditionalRecipients);
  } else if (temporaryManagedRecipients) {
    managedRecipients = parseTemporaryRecipients(temporaryManagedRecipients);
  } else if (formFields[MANAGED_RECIPIENTS]) {
    managedRecipients = formFields[MANAGED_RECIPIENTS];
  } else {
    //When formFields doesn't have managedRecipients, this indicates a schedule-only edit (without going through compose message form)
    // In this case we need to specifically format the recipients payload to match update schedule API reqs
    managedRecipients = formatRecipientsForApi(scheduledMessage[MANAGED_RECIPIENTS]);
  }

  managedRecipients = normalizeManagedRecipientsForUpdate(managedRecipients);
  const shouldIncludeManagedRecipients =
    hasAnyManagedRecipients(managedRecipients) || !scheduledMessage?.senderRecipientOptions;

  const schedule = scheduleFromForm || scheduledMessage[SCHEDULE];

  const convertedFormFields = convertPillsToVariablePlaceholdersInFormFields(formFields);
  const standardVariables = extractStandardVariablesFromFormFields(convertedFormFields);

  return {
    id: scheduledMessage.id,
    [CONFIG]: config,
    [SCHEDULE]: schedule,
    [LANGUAGE]: scheduledMessage[LANGUAGE],
    [TYPE]: SCHEDULED_MESSAGE_TYPES.SCHEDULED,
    [RECIPIENT_COUNT]: recipientCount || scheduledMessage[RECIPIENT_COUNT],
    ...(messageTemplateId && {[MESSAGE_TEMPLATE_ID]: messageTemplateId}),
    ...(shouldIncludeManagedRecipients && !isQuotaMessage && {[MANAGED_RECIPIENTS]: managedRecipients}),
    [STANDARD_VARIABLES]: standardVariables,
  };
}

export function formatMessageRecipientSources({personFilter, recipients, excludedPersonIds, includedPersonIds}) {
  if (personFilter) {
    return {
      messageRecipientSources: {
        filters: [
          {
            personFilter,
            personSource: 'PERSON_SEARCH',
          },
        ],
        excludedPersonIds,
        includedPersonIds,
      },
    };
  } else if (recipients) {
    return {
      messageRecipientSources: {
        includedPersonIds: recipients?.map((recipient) => recipient.personId),
        excludedPersonIds,
      },
    };
  }
  return undefined;
}

/**
 * maps people from alsat response to their respective types
 * @param {*} allRecipients formatted alsat response
 * @param {*} requiredInput formatted requiredInput from templates response
 * @param {*} optionalInput formatted optionalInput from templates response
 * @returns
 */
export function splitRecipientsByCategory(allRecipients, requiredInput = {}, optionalInput = {}) {
  const requiredIds = Object.values(requiredInput).flat();
  const optionalIds = Object.values(optionalInput).flat();

  const requiredRecipients = [];
  const optionalRecipients = [];

  allRecipients.forEach((person) => {
    const id = person.id;

    if (requiredIds.includes(id)) {
      requiredRecipients.push(person);
    } else if (optionalIds.includes(id)) {
      optionalRecipients.push(person);
    }
  });

  return {requiredRecipients, optionalRecipients};
}

// builds recipient fields for scheduling/sending messages based on context
// logic copied from ComposeMessageDrawer
export function buildRecipientFields(
  {
    hasEnhancedRecipientManagement,
    isScheduling,
    selectedTemplate,
    recipients = [],
    groups = [],
    savedSearches = [],
    sites = [],
    additionalRecipients = [],
    additionalGroups = [],
    additionalSavedSearches = [],
    additionalSites = [],
    onlyIncludeAdditionalRecipients,
  },
  form,
) {
  if (!hasEnhancedRecipientManagement) {
    return {};
  }

  const templateDefinition = selectedTemplate?.template || selectedTemplate;
  const templateHasConfiguredRecipients = Boolean(
    countRecipients({senderRecipientOptions: templateDefinition?.senderRecipientOptions}),
  );
  const isTemplateBackedFlow = Boolean(selectedTemplate) || Boolean(onlyIncludeAdditionalRecipients);
  const isSchedulingWithTemplate = !!(isScheduling && selectedTemplate) || onlyIncludeAdditionalRecipients;
  const uniqueIds = (items = [], getId) => uniq(items.map((item) => getId(item)).filter((item) => !isNil(item)));

  // when scheduling a message WITH a template, only use additional recipients...
  // template recipients are handled by template ID on backend
  const additionalRecipientIds = uniqueIds(
    additionalRecipients,
    (recipient) => recipient.id || recipient.value || recipient,
  );
  const additionalGroupIds = uniqueIds(additionalGroups, (recipient) => recipient.id || recipient);
  const additionalSavedSearchIds = uniqueIds(additionalSavedSearches, (recipient) => recipient.id || recipient);

  const recipientIds = uniqueIds(recipients, (recipient) => recipient.id || recipient);
  const groupIds = uniqueIds(groups, (group) => group.id || group);
  const savedSearchesIds = uniqueIds(savedSearches, (savedSearch) => savedSearch.id || savedSearch);

  // Transform sites to include siteId and siteTypes
  const formatSites = (sitesList) => {
    return (
      sitesList?.map((site) => ({
        siteId: site.id || site.siteId,
        siteTypes: [...(site.includePOC ? ['POC'] : []), ...(site.includeOccupants ? ['OCCUPANT'] : [])],
      })) || []
    );
  };

  const mergeUniqueSites = (...siteCollections) => {
    const sitesById = new Map();

    siteCollections.flat().forEach((site) => {
      if (isNil(site?.siteId)) {
        return;
      }

      const previousSite = sitesById.get(site.siteId);

      sitesById.set(site.siteId, {
        ...previousSite,
        ...site,
        siteTypes: uniq([...(previousSite?.siteTypes || []), ...(site.siteTypes || [])]),
      });
    });

    return Array.from(sitesById.values());
  };

  const additionalSiteObjects = formatSites(additionalSites);
  const siteObjects = formatSites(sites);

  const managedRecipients = {
    [PERSON_IDS]: uniq([...recipientIds, ...additionalRecipientIds]),
    [PERSON_GROUP_IDS]: uniq([...groupIds, ...additionalGroupIds]),
    [SAVED_SEARCH_IDS]: uniq([...savedSearchesIds, ...additionalSavedSearchIds]),
    [SITES]: mergeUniqueSites(siteObjects, additionalSiteObjects),
  };

  // Check if all recipient arrays are empty when scheduling with template
  const hasAnyRecipients =
    managedRecipients[PERSON_IDS]?.length ||
    managedRecipients[PERSON_GROUP_IDS]?.length ||
    managedRecipients[SAVED_SEARCH_IDS]?.length ||
    managedRecipients[SITES]?.length;

  const recipientFields = {};

  // Only include managedRecipients if there are actually recipients,
  // or if not scheduling with a template
  if (!isSchedulingWithTemplate || hasAnyRecipients) {
    recipientFields[MANAGED_RECIPIENTS] = managedRecipients;

    return recipientFields;
  }

  // only include additional recipients if NOT scheduling with a selected template
  if (!isSchedulingWithTemplate) {
    recipientFields[ADDITIONAL_RECIPIENTS] = {
      [PERSON_IDS]: additionalRecipientIds,
      [PERSON_GROUP_IDS]: additionalGroupIds,
      [SAVED_SEARCH_IDS]: additionalSavedSearchIds,
      [SITES]: additionalSiteObjects,
    };

    return recipientFields;
  }
  // unfortunately this clumsy else block is needed for the scenario when saving with empty additional recipients
  else if (!isEmpty(recipients) || !isEmpty(groups) || !isEmpty(savedSearches) || !isEmpty(sites)) {
    // This block handles case where form recipients already match current selections
    // When reopening a scheduled message, the form may already contain
    // prefilled recipients under `formattedRequiredRecipients`. In that case,
    // we don’t want to rebuild `managedRecipients` from scratch — we just reuse
    // the existing value from the form to preserve consistency.
    //
    // In this block we check if any of the base recipient arrays (recipients, groups,
    // saved searches, or sites) are non-empty. If they are, we compare them
    // against the form’s current `formattedRequiredRecipients` field that is used for GroupSelect component
    const formattedRequired = form?.getFieldsValue?.()?.formattedRequiredRecipients;
    if (formattedRequired) {
      const expected = {
        [PERSON_IDS]: recipientIds,
        [PERSON_GROUP_IDS]: groupIds,
        [SAVED_SEARCH_IDS]: savedSearchesIds,
        [SITES]: siteObjects,
      };

      // Helper for loose equality: treats empty arrays as equal
      // and compares non-empty ones ignoring order.
      const roughlyEqual = (a, b) => (isEmpty(a) && isEmpty(b) ? true : isEqual(sortBy(a), sortBy(b)));

      // Check that all recipient categories (people, groups, searches, sites)
      // roughly match what’s already in the form.
      const matches =
        roughlyEqual(formattedRequired[PERSON_IDS], expected[PERSON_IDS]) &&
        roughlyEqual(formattedRequired[PERSON_GROUP_IDS], expected[PERSON_GROUP_IDS]) &&
        roughlyEqual(formattedRequired[SAVED_SEARCH_IDS], expected[SAVED_SEARCH_IDS]) &&
        roughlyEqual(formattedRequired[SITES], expected[SITES]);

      // If everything matches, reuse the form’s existing recipients.
      // This avoids unnecessary overwriting when nothing actually changed
      // which was causing issue when saving with empty additional recipients
      if (matches) {
        recipientFields[MANAGED_RECIPIENTS] = formattedRequired;
        return recipientFields;
      }
    }
  } else if (form?.getFieldsValue()?.additionalRecipients) {
    recipientFields[ADDITIONAL_RECIPIENTS] = {
      [PERSON_IDS]: additionalRecipientIds,
      [PERSON_GROUP_IDS]: additionalGroupIds,
      [SAVED_SEARCH_IDS]: additionalSavedSearchIds,
      [SITES]: additionalSiteObjects,
    };

    return recipientFields;
  } else if (form?.getFieldsValue()?.formattedRequiredRecipients && !isTemplateBackedFlow) {
    recipientFields[MANAGED_RECIPIENTS] = form?.getFieldsValue()?.formattedRequiredRecipients;
  } else if (form?.getFieldsValue()?.formattedRequiredRecipients && !templateHasConfiguredRecipients) {
    recipientFields[MANAGED_RECIPIENTS] = form?.getFieldsValue()?.formattedRequiredRecipients;
  }

  return recipientFields;
}

export function parseEncodedRecipients(encodedRecipients = []) {
  const personIds = [];
  const groupIds = [];
  const savedSearchIds = [];
  const siteIds = [];

  encodedRecipients.forEach((encodedValue) => {
    if (encodedValue.includes(':')) {
      const [type, id] = encodedValue.split(':');
      switch (type) {
        case 'person':
          personIds.push(id);
          break;
        case 'group':
          groupIds.push(id);
          break;
        case 'savedSearch':
          savedSearchIds.push(id);
          break;
        case 'site':
          siteIds.push(id);
          break;
        default:
          return null;
      }
    }
  });

  const result = {
    [PERSON_IDS]: personIds,
    [PERSON_GROUP_IDS]: groupIds,
    [SAVED_SEARCH_IDS]: savedSearchIds,
    [SITE_IDS]: siteIds,
  };

  return Object.values(result).every((arr) => arr.length === 0) ? undefined : result;
}

/**
 * Given an organizationId, Gets the highest parent organization (below Root) that the user has access to.
 *
 * If the user has access to his client organization then this will return the client organization that contains the provided organizationId
 * Otherwise it will return the highest possible organization above organizationId
 * @param organizationsDictionary - from state.session.organizationsDictionary This is a dictionary of the organization accessible to the user
 * @param organizationId
 */
export const getTopmostParentOrganization = (organizationsDictionary, organizationId) => {
  if (organizationId) {
    const hierarchyPath = organizationsDictionary[organizationId]?.hierarchyPath;
    // filters out the root org for internal admins and any org without a name, which indicates the user does not have access to that org
    const topmostOrganization = hierarchyPath?.find(
      (organization) => organization.orgId !== DEFAULT_PARENT_ID && !!organization.orgName,
    );

    return topmostOrganization;
  }
};
